import { useClipboard } from "@mantine/hooks";
import { useAuth } from "@/shared/auth/useAuth";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAdminView } from "@/shared/hooks/useAdminView";
import { useHasAdminRole } from "@/shared/hooks/useHasRole";
import { useFollowUpSuggestions } from "@/shared/hooks/useFollowUpSuggestions";

const copyAccessToken = async (
  clipboardCopy: (valueToCopy: any) => void,
  getAccessTokenSilently: () => Promise<string>,
) => {
  const token = await getAccessTokenSilently();
  clipboardCopy("Bearer " + token);
};

export default function GeneralSettings() {
  const { getAccessToken } = useAuth();
  const [isAdminView, toggleAdminView] = useAdminView();
  const clipboard = useClipboard({ timeout: 2000 });
  const isAdmin = useHasAdminRole();
  const { showFollowUpSuggestions, setShowFollowUpSuggestions } =
    useFollowUpSuggestions();

  const handleFollowUpSuggestionsChange = (checked: boolean) => {
    setShowFollowUpSuggestions(checked);
  };

  return (
    <div className="flex-1 px-6 py-3">
      <h2 className="text-lg font-semibold mb-4">General Settings</h2>

      {/* Follow-up Suggestions Toggle */}
      <div className="mb-6">
        <div className="flex items-center space-x-2">
          <Switch
            id="follow-up-suggestions"
            checked={showFollowUpSuggestions}
            onCheckedChange={handleFollowUpSuggestionsChange}
          />
          <Label
            htmlFor="follow-up-suggestions"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Show follow up suggestions in chats
          </Label>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Display suggested questions after assistant responses in chat
          conversations.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {isAdmin && (
          <>
            <Button
              variant="outline"
              onClick={async () =>
                await copyAccessToken(clipboard.copy, getAccessToken)
              }
            >
              {clipboard.copied
                ? "Access token copied!"
                : "Copy JWT access token"}
            </Button>
          </>
        )}
        {isAdmin && (
          <Button
            variant="outline"
            onClick={() => toggleAdminView(!isAdminView)}
          >
            {isAdminView ? "Disable" : "Enable"} Admin View
          </Button>
        )}
      </div>
    </div>
  );
}
